import { extractReceiptData, fileToDataUrl, prepareReceiptImage } from './ocrService';

import { MAX_RECEIPT_CHARS } from '../utils/expenseOptions';

/** First page of a PDF as a PNG data URL, for reading and for the preview. */
export const convertPdfToImage = async (file) => {
    const pdfjsLib = await import('pdfjs-dist');
    // Use matching worker version 5.4.449
    pdfjsLib.GlobalWorkerOptions.workerSrc = 'https://unpkg.com/pdfjs-dist@5.4.449/build/pdf.worker.min.mjs';

    const arrayBuffer = await file.arrayBuffer();
    const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
    const page = await pdf.getPage(1);

    const viewport = page.getViewport({ scale: 2.0 });
    const canvas = document.createElement('canvas');
    canvas.height = viewport.height;
    canvas.width = viewport.width;
    await page.render({ canvasContext: canvas.getContext('2d'), viewport }).promise;
    return canvas.toDataURL('image/png');
};

/**
 * Everything done to one receipt file, shared by the single upload and the bulk import:
 * shrink a phone photo, check the size, draw page 1 of a PDF, read the text.
 *
 * Never throws. Returns
 *   receiptUrl  the data URL to store (the original PDF, or the shrunk image); null if it cannot be attached
 *   previewUrl  an image to show (a PDF is previewed as its first page); null when there is none
 *   ocr         { merchant, amount, date, currency, rawText } or null if the text could not be read
 *   warning     the file is attached but could not be read or previewed
 *   error       the file cannot be attached at all (too large, unreadable)
 *
 * OCR shares one engine, so callers must not run this for two files at the same time.
 */
export const processReceiptFile = async (original, onProgress = () => {}) => {
    const isPDF = original.type === 'application/pdf';
    try {
        // Phone photos are shrunk first: faster to read, and under the server's 5 MB receipt limit.
        const file = isPDF ? original : await prepareReceiptImage(original);
        const receiptUrl = await fileToDataUrl(file);
        if (receiptUrl.length > MAX_RECEIPT_CHARS) {
            return { receiptUrl: null, previewUrl: null, ocr: null, warning: null, error: 'This receipt is too large (5 MB at most). Use a smaller file or a lower-resolution photo.' };
        }

        let imageToRead = file;
        let previewUrl = isPDF ? null : receiptUrl; // Chrome shows a blank frame for a data: PDF, so a PDF is previewed as its first page
        if (isPDF) {
            onProgress(10);
            try {
                previewUrl = await convertPdfToImage(file);
            } catch (pdfErr) {
                console.error('PDF render failed, keeping the file:', pdfErr);
                return { receiptUrl, previewUrl: null, ocr: null, warning: 'The PDF is attached, but it could not be previewed or read. Please fill in the details yourself.', error: null };
            }
            onProgress(30);
            const blob = await (await fetch(previewUrl)).blob();
            imageToRead = new File([blob], 'receipt.png', { type: 'image/png' });
        }

        try {
            const ocr = await extractReceiptData(imageToRead, (p) => onProgress(isPDF ? 30 + p * 0.7 : p));
            return { receiptUrl, previewUrl, ocr, warning: null, error: null };
        } catch (ocrErr) {
            console.error('OCR failed, keeping the receipt:', ocrErr);
            return { receiptUrl, previewUrl, ocr: null, warning: 'The receipt is attached, but its text could not be read. Please fill in the details yourself.', error: null };
        }
    } catch (err) {
        console.error('Error processing receipt:', err);
        return { receiptUrl: null, previewUrl: null, ocr: null, warning: null, error: isPDF ? 'Failed to process PDF. Please try again.' : 'Failed to process receipt. Please try again.' };
    }
};
